import { useState, useEffect } from "react";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Pencil } from "lucide-react";
import { Button, Form, Row, Table } from "react-bootstrap";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { RxCrossCircled } from "react-icons/rx";
import { toast } from "sonner";
import { MyEditor } from "../../../Utils/helpers/MyEditor";
import DeleteModal from "../../DeleteModal";
import { stripHtml } from "../../../Utils/helpers/commonhelper";
import { DescriptionTooltip } from "../../../Utils/helpers/descriptionTooltip/DescriptionTooltip";
import { TableSkeleton } from "../../../Utils/skeletonLoader/TableSkeleton";
import {
  DeleteRequest,
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../../Utils/hooks/ApiHandler";
import Select from "react-select";
import { BsPencil, BsTrash, BsArrowLeft } from "react-icons/bs";
import { formatDate } from "../../../Utils/helpers/commonhelper";

// Toggle Switch Component
const ToggleSwitch = ({ active, onClick }) => {
  return (
    <div
      className={`toggle-switch ${active ? "active" : ""}`}
      onClick={onClick}
      style={{
        width: "30px",
        height: "14px",
        backgroundColor: active ? "#4CAF50" : "#ccc",
        borderRadius: "12px",
        position: "relative",
        cursor: "pointer",
        transition: "background-color 0.3s",
      }}
    >
      <div
        className="toggle-switch-handle"
        style={{
          width: "10px",
          height: "10px",
          backgroundColor: "white",
          borderRadius: "50%",
          position: "absolute",
          top: "2px",
          left: active ? "19px" : "2px",
          transition: "left 0.3s",
        }}
      />
    </div>
  );
};

// Schema definitions
const whatsappSchema = z.object({
  templateName: z.string().min(1, "Template name is required"),
  templateId: z.string().min(1, "Template ID is required"),
  templateContent: z.string().min(1, "Template content is required"),
});

const smsSchema = z.object({
  name: z.string().min(1, "Name is required"),
  templateName: z.string().min(1, "Template name is required"),
  templateId: z.string().min(1, "Template ID is required"),
});

const emailSchema = z.object({
  type: z.string().min(1, "Type is required"),
  subject: z.string().min(1, "Subject is required"),
  templateName: z.string().min(1, "Template  is required"),
  body: z.string().min(1, "Body is required"),
});

// Mock data

const smsTemplates = [
  {
    id: 1,
    name: "Enquiry",
    templateId: "65f05474d6fc0508d3bf812",
    createdDate: "12-02-2025 03:17 PM",
    status: true,
  },
  {
    id: 2,
    name: "Payment",
    templateId: "678b3d3f6fc0508d3bf81b",
    createdDate: "20-02-2025 01:51 PM",
    status: false,
  },
  {
    id: 3,
    name: "Admission",
    templateId: "67c84f1d6fc051ac227d323",
    createdDate: "10-03-2025 04:34 PM",
    status: true,
  },
];

const msgServices = [{ name: "WhatsApp" }, { name: "SMS" }, { name: "Email" }];

const whatsappTemplateOptions = [
  { value: "leads", label: "Lead" },
  { value: "contact", label: "Contact" },
  { value: "company", label: "Company" },
  { value: "invoice", label: "Invoice" }
];

const smsTemplateOptions = [
  { value: "templateA", label: "Template A" },
  { value: "templateB", label: "Template B" },
  { value: "templateC", label: "Template C" },
  { value: "invoice", label: "Invoice" }
];

const emailTypeOptions = [
  { value: "leads", label: "Leads" },
  { value: "contact", label: "Contact" },
  { value: "company", label: "Company" },
  { value: "password", label: "Password" },
  { value: "invoice", label: "Invoice" }
];

export function NotificationTemplates(
  {
    setModuleType,
    moduleType,
    setShow,
    show,
    setAddTemplate,
    setSelectedTemplate,
    setWhatsappTemplateDataOption,
  }) {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer");
  const customerId = customer?.customer_details?.customer_id;
  const loginUserId = customer?.customer_details?.id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    authentication: rf,
    Authorization: token,
  };

  const extras = { customer, setCustomer, rf, navigate };

  // Initialize activeTab from localStorage or default to "WhatsApp"
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("notificationActiveTab") || "WhatsApp";
    }
    return "WhatsApp";
  });

  // permission for user management
  const permission = customer.customer_details?.permission
  const settingPermision = permission?.filter(c => c.module_name === "setting")
  const user_type = Number(customer.customer_details?.user_type)
  const preferenceGrant = {
    isView: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name === "preference")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name === "preference")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name === "preference")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name === "preference")?.delete)) : true
  }

  const [currentTemplates, setCurrentTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [isSMSModalOpen, setIsSMSModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [saveButtonD, setSaveButtonD] = useState(false);
  const [emailTemplateData, setEmailTemplateData] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [emailTemplatesTable, setEmailTemplatesTable] = useState([]);
  const [whatsappTableData, setWhatsappTableData] = useState([]);
  const [whatsappToDelete, setWhatsappToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [whatsappTemplateData, setWhatsappTemplateData] = useState(null);
  const [dynamicFields, setDynamicFields] = useState([]);
  // const [saveButtonD, setSaveButtonD] = useState(false);
  // Update localStorage whenever activeTab changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("notificationActiveTab", activeTab);
    }
    resetFormStates(); // Reset forms when tab changes
  }, [activeTab]);

  // Create dynamic schema for WhatsApp form based on dynamic fields
  const createWhatsAppSchema = () => {
    const dynamicFieldValidations = {};

    dynamicFields.forEach((field) => {
      if (field.type === "image") {
        dynamicFieldValidations[field.name] = z
          .instanceof(FileList)
          .refine((files) => files?.length > 0, "Image is required");
      } else if (field.type === "url") {
        dynamicFieldValidations[field.name] = z
          .string()
          .min(1, `${field.label} is required`)
          .url("Please enter a valid URL");
      } else {
        dynamicFieldValidations[field.name] = z
          .string()
          .min(1, `${field.label} is required`);
      }
    });

    return whatsappSchema.extend(dynamicFieldValidations);
  };

  // Form hooks
  const whatsappForm = useForm({
    resolver: zodResolver(createWhatsAppSchema()),
  });

  const smsForm = useForm({
    resolver: zodResolver(smsSchema),
  });

  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      type: "",
      subject: "",
      body: "",
    },
  });

  // Function to reset all form states
  const resetFormStates = () => {
    setIsEditMode(false);
    setCurrentTemplateId(null);
    setEmailTemplateData(null);
    setWhatsappTemplateData(null);

    setDynamicFields([]);

    // Reset all forms
    whatsappForm.reset();
    smsForm.reset();
    emailForm.reset({
      type: "",
      subject: "",
      body: "",
    });
  };

  // Load templates based on active tab
  useEffect(() => {
    let data = [];

    if (activeTab === "WhatsApp") {
      data = whatsappTableData;
    } else if (activeTab === "SMS") {
      data = smsTemplates;
    } else if (activeTab === "Email") {
      data = emailTemplatesTable;
    }
    setCurrentTemplates(data);

  }, [activeTab, whatsappTableData, emailTemplatesTable]);

  // Fetch email template table data
  const fetchEmailTableData = async () => {
    setIsTableLoading(true);
    try {
      const res = await GetRequest(
        `/integration/fetchEmailTemplates/${customerId}`,
        { headers },
        extras
      );

      setEmailTemplatesTable(res.data || []);
    } catch (error) {
      console.error("Error fetching email templates:", error);
      toast.error("Failed to fetch email templates");
    } finally {
      setIsTableLoading(false);
    }
  };

  // Fetch whatsapp table data
  const fetchWhatsappTableData = async () => {
    setIsTableLoading(true);
    try {
      const res = await GetRequest(
        `/integration/fetchAllMsg91Template/${customerId}`,
        { headers },
        extras
      );

      setWhatsappTableData(res.data || []);
    } catch (error) {
      console.error("Error fetching email templates:", error);
      toast.error("Failed to fetch email templates");
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "Email") {
      fetchEmailTableData();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "WhatsApp") {
      fetchWhatsappTableData();
    }
  }, [activeTab]);

  const onWhatsAPPFetch = async () => {
    const templateId = whatsappForm.getValues("templateId");
    if (!templateId) {
      toast.error("Please enter a template ID first");
      return;
    }

    setIsLoading(true);
    try {
      const res = await GetRequest(
        `/integration/fetchWhatsappTemplateContent/${templateId}/${customerId}`,
        { headers },
        extras
      );

      if (res.status === "success") {
        setSaveButtonD(false)
        const templateData = JSON.parse(res.data);
        setSaveButtonD(false);


        setWhatsappTemplateData(templateData.data);


        // Find the BODY content
        const bodyContent = templateData.data[0].languages[0].code.find(
          (item) => item.type === "BODY"
        );
        if (bodyContent) {
          whatsappForm.setValue("templateContent", bodyContent.text || "");
        }

        // Extract variables and their types
        const variables = templateData.data[0].languages[0].variables || [];
        const variableTypes =
          templateData.data[0].languages[0].variable_type || {};

        // Create dynamic fields based on variables

        let count = 0;
        const fields = variables.map((variable) => {
          const varType = variableTypes[variable]?.type;
          let fieldType = "text";

          let label = variable;

          if (variable.startsWith("header_")) {
            count++;
            fieldType = varType;
            label = "Header";
          } else if (variable.startsWith("button_")) {
            count++;
            fieldType = "url";
            label = "Button URL";
          } else if (variable.startsWith("body_")) {
            count++;
            label = `Body Text ${variable.split("_")[1]}`;
          }

          return {
            name: variable,
            type: fieldType,
            label: label,
            required: true,
          };
        });

        setDynamicFields(fields);

        toast.success("Template fetched successfully");
      } else {
        toast.error(res.message || "Failed to fetch template");
      }
    } catch (error) {
      console.error("Error fetching WhatsApp template:", error);
      toast.error("Failed to fetch template");
    } finally {
      setIsLoading(false);
    }
  };
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      fontSize: 13,
      padding: "0 4px",
      boxShadow: "none",
      border: state.isFocused ? "1px solid #2684FF" : "1px solid #d1d5db",
      "&:hover": {
        borderColor: "#60a5fa",
      },
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "0 4px",
    }),

    input: (base) => ({
      ...base,
      padding: "2px 0",
      margin: 0,
    }),

    placeholder: (base) => ({
      ...base,
      fontSize: 13,
      paddingLeft: "10px",
    }),

    singleValue: (base) => ({
      ...base,
      fontSize: 13,
      paddingLeft: "10px",
    }),

    option: (base) => ({
      ...base,
      fontSize: 13,
      padding: "6px 10px",
    }),

    menuList: (base) => ({
      ...base,
      maxHeight: 200,
      overflowY: "auto",
      scrollBehavior: "smooth",
      "::-webkit-scrollbar": {
        width: "8px",
      },
      "::-webkit-scrollbar-track": {
        background: "#f1f1f1",
        borderRadius: "4px",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#c9c9c9",
        borderRadius: "4px",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#888",
      },
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),

    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),

    multiValue: (base) => ({
      ...base,
      backgroundColor: "#3b82f6",
      color: "white",
      fontSize: 12,
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      fontWeight: 500,
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": {
        backgroundColor: "#ef4444",
        color: "white",
      },
    }),
  };

  // Handle edit template
  const handleEditTemplate = async (template) => {

    if (activeTab === "WhatsApp") {

    }
    if (activeTab === "SMS") {

    }
    setIsLoading(true);
    setIsEditMode(true);
    setCurrentTemplateId(template.id);

    try {
      let res;
      if (activeTab === "Email") {
        setIsEmailModalOpen(true);
        res = await GetRequest(
          `/integration/fetchTemplateDetails/${template.id}/${customerId}`,
          { headers },
          extras
        );

        const data = res.data[0];
        if (res.status === "success") {

          emailForm.reset({
            type: data.type,
            templateName: data.template_name,
            subject: data.subject,
            body: data.body,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching template:", error);
      toast.error("Failed to fetch template data");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (templateId, currentStatus) => {
    try {
      setIsLoading(true);
      const res = await DeleteRequest(
        `/integration/deleteEmailTemplate/${templateId}/${customerId}/${currentStatus}`,
        { headers },
        extras
      );

      if (res.status === "success") {
        toast.success(res.message);
        fetchEmailTableData();
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating template status:", error);
      toast.error("Failed to update template status");
    } finally {
      setIsLoading(false);
    }
  };

  // Form submission handler
  const onSubmit = async (data) => {
    setIsLoading(true);

    try {

      if (activeTab === "Email") {
        const payload = {
          body: data.body,
          subject: data.subject,
          type: data.type,
          customer_id: customerId,
          user_login_id: loginUserId,
          template_name: data.templateName,
        };

        let res;
        if (isEditMode) {
          const editPayload = {
            template_id: currentTemplateId,
            customer_id: customerId,
            type: data.type,
            subject: data.subject,
            body: data.body,
            template_name: data.templateName,
          };
          res = await PutRequest(
            `/integration/updateEmailTemplate/${currentTemplateId}`,
            editPayload,
            { headers },
            extras
          );
        } else {
          res = await PostRequest(
            `/integration/insertEmailTemplate`,
            payload,
            false,
            { headers },
            extras
          );
        }

        if (res.status === "success") {
          toast.success(
            `Email template ${isEditMode ? "updated" : "added"} successfully!`
          );
          moduleType && setAddTemplate(false);
          moduleType && setModuleType(null);
          moduleType && setShow(false);
          fetchEmailTableData();
          setIsEmailModalOpen(false);
          resetFormStates();
        } else {
          toast.error(
            res.message || `Failed to ${isEditMode ? "update" : "add"} template`
          );
        }
      } else if (activeTab === "WhatsApp") {
        const variables = [];
        const variableValues = [];
        const variableTypes = [];
        let bodyCount = 0;

        dynamicFields.forEach((field) => {
          variables.push(field.name);
          variableTypes.push(field.type);

          if (field.name.startsWith("body_")) {
            bodyCount++;
          }

          // Handle file inputs differently
          if (field.type === "image") {
            // For files, we might need to upload them separately or convert to base64
            // Here we're just getting the file name for demonstration
            variableValues.push(data[field.name]?.[0]?.name || "");
          } else {
            variableValues.push(data[field.name] || "");
          }
        });

        // Prepare the payload according to API requirements
        const payload = {
          customer_id: customerId,
          module_name: whatsappForm.getValues("templateName"),
          template_name: whatsappTemplateData?.[0]?.name || "",
          template_namespace: whatsappTemplateData?.[0]?.namespace || "",
          template_language:
            whatsappTemplateData?.[0]?.languages?.[0]?.language || "en",
          template_template_id:
            whatsappTemplateData?.[0]?.languages?.[0]?.id || "",
          template_variable_count: variables.length.toString(),
          template_variable_type: variables,
          template_variable_typeOFtype: variableTypes,
          template_body_count: bodyCount.toString(),
          template_content: data.templateContent,
          variables_value: variableValues,
          user_login_id: loginUserId,
        };


        let res;
        if (isEditMode) {
          res = await PutRequest(
            `/integration/updateWhatsAppTemplate/${currentTemplateId}`,
            payload,
            { headers },
            extras
          );
        } else {
          res = await PostRequest(
            `/integration/insertMsg91Template`,
            payload,
            true,
            { headers },
            extras
          );
        }

        if (res.status === "success") {
          toast.success(
            `WhatsApp template ${isEditMode ? "updated" : "added"
            } successfully!`
          );
          await fetchWhatsappTableData();
          setIsWhatsAppModalOpen(false);
          moduleType && setAddTemplate(false);
          moduleType && setModuleType(null);
          moduleType && setShow(false);
          resetFormStates();
        } else {
          toast.error(
            res.message || `Failed to ${isEditMode ? "update" : "add"} template`
          );
        }
      } else if (activeTab === "SMS") {
        toast.success(
          `${activeTab} template ${isEditMode ? "updated" : "added"
          } successfully!`
        );
        setIsSMSModalOpen(false);
        resetFormStates();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(`Failed to ${isEditMode ? "update" : "submit"} template`);
    } finally {
      setIsLoading(false);
    }
  };

  // Open appropriate modal based on active tab
  const handleAddTemplate = () => {
    resetFormStates();

    if (activeTab === "WhatsApp") {
      setSaveButtonD(true)
      setIsWhatsAppModalOpen(true);
      setSaveButtonD(true);
    } else if (activeTab === "SMS") {
      setIsSMSModalOpen(true);
    } else if (activeTab === "Email") {
      setIsEmailModalOpen(true);
    }
  };
  useEffect(() => {
    resetFormStates();

    if (moduleType === "WhatsApp") {
      setIsWhatsAppModalOpen(true);
      setSaveButtonD(true);
      setActiveTab("WhatsApp");
    } else if (moduleType === "SMS") {
      setIsSMSModalOpen(true);
      setActiveTab("WhatsApp");
    } else if (moduleType === "Email") {
      setIsEmailModalOpen(true);
      setActiveTab("Email");
    }
  }, [moduleType]);


  // Handle delete confirmation
  const onDelete = (id) => {
    setWhatsappToDelete(id);
    setShowDeleteModal(true);
  };
  const onEmailDelete = (id) => {
    setWhatsappToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmedDelete = async (id) => {
    setIsLoading(true)
    try {
      const item = whatsappTableData.find((d) => d.id === id);
      if (!item) return;

      const res = await DeleteRequest(
        `/integration/deleteMsg91Template/${id}`,
        { headers },
        extras
      );
      setShowDeleteModal(false);

      if (res.status === "success") {
        await fetchWhatsappTableData();
        setIsLoading(false)
        toast.success(res.message);

        setWhatsappTableData((prev) =>
          prev.filter((item) => item.id !== id)
        );
      }
    } catch (error) {
      console.error(error);
    }
  };
  // for email 
  const handleConfirmedEmailDelete = async (id) => {
    setIsLoading(true)
    try {
      const res = await DeleteRequest(
        `/integration/delete_EmailTemplate/${id}/${customerId}`,
        { headers },
        extras
      );

      setShowDeleteModal(false);

      if (res.status === "success") {
        setIsLoading(false)
        toast.success(res.message);
        setEmailTemplatesTable((prev) =>
          prev.filter((item) => item.id !== id)
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Render dynamic fields based on template variables
  const renderDynamicFields = () => {
    return dynamicFields.map((field, index) => {
      if (field.type === "image") {
        return (
          <div key={index} className="col-md-12">
            <label className="form-label">
              {field.label} <span className="text-danger">*</span>
            </label>
            <input
              type="file"
              {...whatsappForm.register(field.name, {
                required: `${field.label} is required`,
              })}
              className={`form-control mb-2 `}
              accept="image/*"
            />
            {whatsappForm.formState.errors[field.name] && (
              <div className="text-danger small mt-1">
                {whatsappForm.formState.errors[field.name].message}
              </div>
            )}
          </div>
        );
      } else if (field.type === "url") {
        return (
          <div key={index} className="col-md-12">
            <label className="form-label">
              {field.label} <span className="text-danger">*</span>
            </label>
            <input
              type="url"
              {...whatsappForm.register(field.name, {
                required: `${field.label} is required`,
                validate: (value) => {
                  try {
                    new URL(value);
                    return true;
                  } catch {
                    return "Please enter a valid URL";
                  }
                },
              })}
              className={`form-control mb-2 `}
              placeholder={`Enter ${field.label}`}
            />
            {whatsappForm.formState.errors[field.name] && (
              <div className="text-danger small mt-1">
                {whatsappForm.formState.errors[field.name].message}
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div key={index} className="col-md-12">
            <label className="form-label">
              {field.label} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              {...whatsappForm.register(field.name, {
                required: `${field.label} is required`,
              })}
              className={`form-control mb-2 `}
              placeholder={`Enter ${field.label}`}
            />
            {whatsappForm.formState.errors[field.name] && (
              <div className="text-danger small mt-1">
                {whatsappForm.formState.errors[field.name].message}
              </div>
            )}
          </div>
        );
      }
    });
  };

  // Render table content based on active tab
  // Render table content based on active tab
  const renderTableContent = () => {
    if (!preferenceGrant?.isView) {
      return <>You are not authorized to view tempate listing!</>
    } else if (activeTab === "WhatsApp") {
      return (
        <>
          <DeleteModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => handleConfirmedDelete(whatsappToDelete)}
            isLoading={isLoading}
            message="Are you sure you want to delete this item?"
          />
          {isTableLoading ? (
            <div className="mb-3">
              <TableSkeleton columnCount={6} rowCount={5} />
            </div>
          ) : (
            <>
              <thead style={{ backgroundColor: "#f3effe" }}>
                <tr>
                  <th className="text-danger">S.No</th>
                  <th className="text-black">NAME</th>
                  <th className="text-black">DESCRIPTION</th>
                  <th className="text-black">LANGUAGE</th>
                  <th className="text-black">CREATED ON</th>
                  <th className="text-black">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {whatsappTableData.length > 0 ? (
                  whatsappTableData.map((template, index) => (
                    <tr key={template.id}>
                      <td>{index + 1}</td>
                      <td>{template.template_name || "-"}</td>
                      <td>
                        <DescriptionTooltip
                          text={stripHtml(template.content) || "-"}
                          maxLength={20}
                        />
                      </td>
                      <td>{template.template_language}</td>
                      <td>{template.created_at}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            title="Delete"
                            onClick={() => {
                              if (!preferenceGrant?.isDelete) {
                                toast.warning("You are not authorized for this action!")
                                return
                              }
                              onDelete(template.id)
                            }}
                          >
                            <BsTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-3">
                      No data found !!
                    </td>
                  </tr>
                )}
              </tbody>
            </>
          )}
        </>
      );
    } else if (activeTab === "SMS") {
      return (
        <>
          <DeleteModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => handleConfirmedDelete(whatsappToDelete)}
            isLoading={isLoading}
            message="Are you sure you want to delete this followup?"
          />
          <thead style={{ backgroundColor: "#f3effe" }}>
            <tr>
              <th>S.No</th>
              <th>NAME</th>
              <th>TEMPLATE ID</th>
              <th>CREATED ON</th>
              <th>STATUS</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {currentTemplates.map((template, index) => (
              <tr key={template.id}>
                <td>{index + 1}</td>
                <td>{template.name}</td>
                <td>{template.templateId}</td>
                <td>{template.createdDate}</td>
                <td>
                  <ToggleSwitch
                    active={template.status}
                    onClick={() =>
                      handleStatusToggle(template.id, template.status)
                    }
                  />
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => handleEditTemplate(template)}
                    >
                      <BsPencil />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      title="Delete"
                    // onClick={() => onDelete(template.id)}
                    >
                      <BsTrash />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </>
      );
    } else if (activeTab === "Email") {
      return (
        <>
          <DeleteModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => handleConfirmedEmailDelete(whatsappToDelete)}
            isLoading={isLoading}
            message="Are you sure you want to delete this item?"
          />
          {isTableLoading ? (
            <div className="mb-3">
              <TableSkeleton columnCount={7} rowCount={5} />
            </div>
          ) : (
            <>
              <thead style={{ backgroundColor: "#f3effe" }}>
                <tr>
                  <th>S.No</th>
                  <th>TYPE</th>
                  <th>SUBJECT</th>
                  <th>BODY</th>
                  <th>CREATED ON</th>
                  <th>STATUS</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {emailTemplatesTable.length > 0 ? (
                  emailTemplatesTable.map((template, index) => (
                    <tr key={template.id}>
                      <td>{index + 1}</td>
                      <td>{template.type}</td>
                      <td>{template.subject}</td>
                      <td>
                        <DescriptionTooltip
                          text={stripHtml(template.body) || "-"}
                          maxLength={20}
                        />
                      </td>
                      <td>{formatDate(template.created_at)}</td>
                      <td>
                        <ToggleSwitch
                          active={template.status === "Active"}
                          onClick={() =>
                            handleStatusToggle(template.id, template.status)
                          }
                        />
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              if (!preferenceGrant?.isEdit) {
                                toast.warning("You are not authorized for this action")
                                return
                              }
                              handleEditTemplate(template)
                            }}
                          >
                            <BsPencil />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-3">
                      No data found !!
                    </td>
                  </tr>
                )}
              </tbody>
            </>
          )}
        </>
      );
    }
    return null;
  };
  return (
    <div className="p-4 position-relative">
      {/* Header Section */}
      <div className="mb-4 ms-1">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
          <div className="d-flex flex-column text-start">
            <h2 className="h4 fw-bold mb-2">Notification Templates</h2>
            <span className="text-muted mb-1">
              You can manage notification templates for WhatsApp, SMS, and
              Email.
            </span>
          </div>
          <Button
            // variant="outline-primary"

            onClick={() => navigate(-1)}
            className="mt-4 mt-md-0 btn btn-default-outline"
          >
            <ArrowLeft className="me-2 h-4 w-4" /> Back
          </Button>
        </div>
        <hr className="text-muted" />
        {/* Main Content Area */}
        <div className="container-table">
          <div className="bg-white rounded  border p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              {/* Tabs */}
              <div
                className="btn-group mb-3 mb-md-0 d-flex gap-2"
                role="group"
                style={{ height: "38px" }}
              >
                {msgServices.map((service) => (
                  <Button
                    key={service.name}
                    variant={
                      activeTab === service.name ? "primary" : "outline-primary"
                    }
                    onClick={() => setActiveTab(service.name)}
                    className="rounded"
                  >
                    {service.name}
                  </Button>
                ))}
              </div>

              {/* Add Template Button */}
              <div className="mt-1">
                <Button className="btn btn-default" onClick={() => {
                  if (!preferenceGrant?.isCreate) {
                    toast.warning("You are not authorized for this action!")
                    return
                  }
                  handleAddTemplate()
                }}>
                  Add Templates
                </Button>
              </div>
            </div>

            {/* Template List Table */}
            <div className="table-responsive">
              <Table hover responsive className="align-middle">
                {renderTableContent()}
              </Table>
            </div>
          </div>
        </div>

        {/* WhatsApp Modal */}
        {isWhatsAppModalOpen && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
            onClick={() => {
              setIsWhatsAppModalOpen(false)
              resetFormStates()
              moduleType && setAddTemplate(false);
              moduleType && setModuleType(null);
              moduleType && setShow(false);
            }}
          >
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content px-2">
                <div className="modal-header border-bottom pb-2">
                  <div className="d-flex align-items-start justify-content-between w-100">
                    <div>
                      <p className="modal-title text-dark fs-17 fw-400">
                        {isEditMode ? "Edit" : "Add"} WhatsApp Template
                      </p>
                      <small
                        className="text-muted fw-300 fs-12"
                        style={{ lineHeight: "normal" }}
                      >
                        {isEditMode ? "Edit" : "Add a new"} WhatsApp template for
                        notifications
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setIsWhatsAppModalOpen(false)
                        resetFormStates();
                        moduleType && setAddTemplate(false);
                        moduleType && setModuleType(null);
                        moduleType && setShow(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "1.8rem",
                        cursor: "pointer",
                        color: "#888",
                        marginTop: "-20px",
                        opacity: "0.4",
                      }}
                    >
                      <RxCrossCircled />
                    </button>
                  </div>
                </div>
                <div className="modal-body pt-3">
                  {isLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <Form className="form-container" onSubmit={whatsappForm.handleSubmit(onSubmit)}>
                      <Row className="row g-3">
                        <Form.Group className="col-md-12 focus-z-index position-relative">
                          <Form.Label className="form-label" style={{ marginLeft: '11px' }}>
                            Select Template Name{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Controller
                            name="templateName"
                            control={whatsappForm.control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={whatsappTemplateOptions}
                                placeholder="Select Template Name"
                                className={`react-select-container`}
                                styles={customSelectStyles}
                                value={whatsappTemplateOptions.find(
                                  (option) => option.value === field.value
                                )}
                                classNamePrefix="react-select"
                                onChange={(option) =>
                                  field.onChange(option?.value)
                                }
                                isClearable={true}
                              />
                            )}
                          />
                          {whatsappForm.formState.errors.templateName && (
                            <div className="text-danger small mt-1">
                              {whatsappForm.formState.errors.templateName.message}
                            </div>
                          )}
                        </Form.Group>

                        <Form.Group className="col-md-12 focus-z-index position-relative">
                          <Form.Label className="form-label" style={{ marginLeft: '11px' }}>
                            Template ID <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              {...whatsappForm.register("templateId")}
                              className={`form-control `}
                              placeholder="Enter template ID"
                            />
                            <Button
                              className="btn btn-default"
                              onClick={onWhatsAPPFetch}
                              disabled={
                                isLoading || !whatsappForm.watch("templateId")
                              }
                            >
                              {isLoading ? "Fetching..." : "Fetch"}
                            </Button>
                          </div>
                          {whatsappForm.formState.errors.templateId && (
                            <div className="text-danger small mt-1">
                              {whatsappForm.formState.errors.templateId.message}
                            </div>
                          )}
                        </Form.Group>

                        <Form.Group className="col-md-12 focus-z-index position-relative">
                          <Form.Label className="form-label" style={{ marginLeft: '11px' }}>
                            Template Content{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <textarea
                            {...whatsappForm.register("templateContent")}
                            className={`form-control `}
                            rows={4}
                            placeholder="Enter template content"
                            value={
                              whatsappTemplateData?.content ||
                              whatsappForm.watch("templateContent")
                            }
                            onChange={(e) =>
                              whatsappForm.setValue(
                                "templateContent",
                                e.target.value
                              )
                            }
                          />
                          {whatsappForm.formState.errors.templateContent && (
                            <div className="text-danger small mt-1">
                              {
                                whatsappForm.formState.errors.templateContent
                                  .message
                              }
                            </div>
                          )}
                        </Form.Group>

                        {/* Dynamic Fields Section */}
                        {dynamicFields.length > 0 && (
                          <div className="col-md-12">
                            <h5 className="mb-3">Template Variables</h5>
                            <div className="row g-3">{renderDynamicFields()}</div>
                          </div>
                        )}
                      </Row>
                      <hr />
                      <div className="d-flex justify-content-end gap-2 mt-1">
                        <button
                          type="button"
                          className="btn btn-default-outline"
                          onClick={() => {
                            setIsWhatsAppModalOpen(false)
                            resetFormStates()
                            moduleType && setAddTemplate(false);
                            moduleType && setModuleType(null);
                            moduleType && setShow(false);
                          }}
                          disabled={isLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-default"
                          // style={{
                          //   backgroundColor: "rgb(122,58,94)",
                          //   color: "white",
                          // }}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              {isEditMode ? "Updating..." : "Saving..."}
                            </>
                          ) : isEditMode ? (
                            "Update"
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SMS Modal */}
        {isSMSModalOpen && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
            onClick={() => {
              setIsSMSModalOpen(false)
              resetFormStates()
            }}
          >
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content px-2">
                <div className="modal-header border-bottom pb-2">
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <h5 className="modal-title text-dark fs-17 fw-400">
                        {isEditMode ? "Edit" : "Add"} SMS Template
                      </h5>
                      <small className="text-muted fw-300 fs-12">
                        {isEditMode ? "Edit" : "Add a new"} SMS template for
                        notifications
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setIsSMSModalOpen(false)
                        resetFormStates()
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "1.8rem",
                        cursor: "pointer",
                        color: "#888",
                        marginTop: "-20px",
                        opacity: "0.4",
                      }}
                    >
                      <RxCrossCircled />
                    </button>
                  </div>
                </div>
                <div className="modal-body pt-3">
                  {isLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <Form className="form-container" onSubmit={smsForm.handleSubmit(onSubmit)}>
                      <Row className="row g-3">
                        <Form.Group className="col-md-12 focus-z-index position-relative">
                          <Form.Label className="form-label" style={{ marginLeft: '11px' }}>
                            Name <span className="text-danger">*</span>
                          </Form.Label>
                          <input
                            type="text"
                            {...smsForm.register("name")}
                            className={`form-control ${smsForm.formState.errors.name ? "is-invalid" : ""
                              }`}
                            placeholder="Template name"
                          />
                          {smsForm.formState.errors.name && (
                            <div className="text-danger small mt-1">
                              {smsForm.formState.errors.name.message}
                            </div>
                          )}
                        </Form.Group>

                        <Form.Group className="col-md-12 focus-z-index position-relative">
                          <Form.Label className="form-label" style={{ marginLeft: '11px' }}>
                            Select Template Name{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Controller
                            name="templateName"
                            control={smsForm.control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={smsTemplateOptions}
                                placeholder="Select Template Name"
                                className={`react-select-container ${smsForm.formState.errors.templateName
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                styles={customSelectStyles}
                                value={smsTemplateOptions.find(
                                  (option) => option.value === field.value
                                )}
                                classNamePrefix="react-select"
                                onChange={(option) =>
                                  field.onChange(option?.value)
                                }
                              />
                            )}
                          />
                          {smsForm.formState.errors.templateName && (
                            <div className="text-danger small mt-1">
                              {smsForm.formState.errors.templateName.message}
                            </div>
                          )}
                        </Form.Group>

                        <Form.Group className="col-md-12 focus-z-index position-relative">
                          <Form.Label className="form-label" style={{ marginLeft: "10px" }}>
                            Template ID <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              {...smsForm.register("templateId")}
                              className={`form-control ${smsForm.formState.errors.templateId
                                ? "is-invalid"
                                : ""
                                }`}
                              placeholder="Enter template ID"
                            />
                            <Button className="btn btn-default">Fetch</Button>
                          </div>
                          {smsForm.formState.errors.templateId && (
                            <div className="text-danger small mt-1">
                              {smsForm.formState.errors.templateId.message}
                            </div>
                          )}
                        </Form.Group>
                      </Row>
                      <hr />
                      <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                          type="button"
                          className="btn btn-default-outline"
                          onClick={() => {
                            setIsSMSModalOpen(false)
                            resetFormStates()
                          }}
                          disabled={isLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn"
                          style={{
                            backgroundColor: "rgb(122,58,94)",
                            color: "white",
                          }}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              {isEditMode ? "Updating..." : "Saving..."}
                            </>
                          ) : isEditMode ? (
                            "Update"
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Modal */}
        {isEmailModalOpen && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
            onClick={() => {
              setIsEmailModalOpen(false)
              resetFormStates();
              moduleType && setAddTemplate(false);
              moduleType && setModuleType(null);
              moduleType && setShow(false);
            }}
          >
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content px-2">
                <div className="modal-header border-bottom pb-2">
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <h5 className="modal-title text-dark fs-17 fw-400">
                        {isEditMode ? "Edit" : "Add"} Email Template
                      </h5>
                      <small className="text-muted fw-300 fs-12">
                        {isEditMode ? "Edit" : "Add a new"} Email template for
                        notifications
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setIsEmailModalOpen(false);
                        resetFormStates();
                        moduleType && setAddTemplate(false);
                        moduleType && setModuleType(null);
                        moduleType && setShow(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "1.8rem",
                        cursor: "pointer",
                        color: "#888",
                        marginTop: "-20px",
                        opacity: "0.4",
                      }}
                    >
                      <RxCrossCircled />
                    </button>
                  </div>
                </div>
                <div className="modal-body pt-3">
                  <Form className="form-container" onSubmit={emailForm.handleSubmit(onSubmit)}>
                    <Row className="row g-3">
                      <Form.Group className="col-md-12 focus-z-index position-relative ">
                        <Form.Label className="form-label" style={{ marginLeft: '11px' }}>
                          Type <span className="text-danger">*</span>
                        </Form.Label>
                        <Controller
                          name="type"
                          control={emailForm.control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              isClearable={true}
                              options={emailTypeOptions}
                              placeholder="Select Type"
                              styles={customSelectStyles}
                              className={`react-select-container ${emailForm.formState.errors.type
                                ? "is-invalid"
                                : ""
                                }`}
                              classNamePrefix="react-select"
                              value={emailTypeOptions.find(
                                (option) => option.value === field.value
                              )}
                              onChange={(option) => {
                                field.onChange(option?.value)
                              }}
                            />
                          )}
                        />
                        {emailForm.formState.errors.type && (
                          <div className="text-danger small mt-1">
                            {emailForm.formState.errors.type.message}
                          </div>
                        )}
                      </Form.Group>

                      {/* <Row className="row g-3"> */}
                      <Form.Group className="col-md-12 focus-z-index position-relative">
                        <Form.Label style={{ marginLeft: '10px' }}>
                          Template Name <span className="text-danger">*</span>
                        </Form.Label>
                        <input
                          type="text"
                          {...emailForm.register("templateName")}
                          className={`form-control `}
                          placeholder="Template name "
                        />
                        {emailForm.formState.errors.templateName && (
                          <div className="text-danger small mt-1">
                            {emailForm.formState.errors.templateName.message}
                          </div>
                        )}
                      </Form.Group>
                      <Form.Group className="col-md-12 focus-z-index position-relative">
                        <Form.Label className="form-label " style={{ marginLeft: '10px' }}>
                          Subject <span className="text-danger">*</span>
                        </Form.Label>
                        <input
                          type="text"
                          {...emailForm.register("subject")}
                          className={`form-control`}
                          placeholder="Email subject"
                        />
                        {emailForm.formState.errors.subject && (
                          <div className="text-danger small mt-1">
                            {emailForm.formState.errors.subject.message}
                          </div>
                        )}
                      </Form.Group>

                      <Form.Group className="col-md-12 ">
                        <Form.Label className="focus-z-index position-relative">
                          Body <span className="text-danger">*</span>
                        </Form.Label>
                        <Controller
                          name="body"
                          control={emailForm.control}
                          render={({ field }) => (
                            <MyEditor
                              initialData={
                                emailTemplateData?.body || field.value
                              }
                              onDataChange={(data) => {
                                field.onChange(data)
                              }}
                            />
                          )}
                        />
                        {emailForm.formState.errors.body && (
                          <div className="text-danger small mt-1">
                            {emailForm.formState.errors.body.message}
                          </div>
                        )}
                      </Form.Group>
                      {/* </Row> */}

                      <div className="col-md-12 mt-4">
                        <h3 className="h5 mb-2">Notes</h3>
                        <Table bordered>
                          <thead>
                            <tr>
                              <th>SYNTAX</th>
                              <th>FIELD</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{"{{to_company_name}}"}</td>
                              <td>Company Name</td>
                            </tr>
                            <tr>
                              <td>{"{{sender_name}}"}</td>
                              <td>Sender Name</td>
                            </tr>
                            <tr>
                              <td>{"{{sender_company_name}}"}</td>
                              <td>Sender Compnay Name</td>
                            </tr>
                            <tr>
                              <td>{"{{sender_email}}"}</td>
                              <td>Sender Email</td>
                            </tr>
                            <tr>
                              <td>{"{{sender_number}}"}</td>
                              <td>Sender Number</td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Row>
                    <hr />
                    <div className="d-flex justify-content-end gap-2 mt-1">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setIsEmailModalOpen(false);
                          resetFormStates();
                          moduleType && setAddTemplate(false);
                          moduleType && setModuleType(null);
                          moduleType && setShow(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn"
                        style={{
                          backgroundColor: "rgb(122,58,94)",
                          color: "white",
                        }}
                      >
                        {isLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            {isEditMode ? "Updating..." : "Saving..."}
                          </>
                        ) : isEditMode ? (
                          "Update"
                        ) : (
                          "Save"
                        )}
                      </button>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  )
}
